import { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'
import { FEATURES } from '@gnosis.pm/safe-react-gateway-sdk'

import { ListItemType } from 'src/components/List'
import ListIcon from 'src/components/List/ListIcon'
import { currentSafeFeaturesEnabled, currentSafeWithNames } from 'src/logic/safe/store/selectors'
import { grantedSelector } from 'src/routes/safe/container/selector'
import { ADDRESSED_ROUTE, SAFE_SUBSECTION_ROUTE, generatePrefixedAddressRoutes } from 'src/routes/routes'
import { hasFeature } from 'src/logic/safe/utils/safeVersion'
import useSafeAddress from 'src/logic/currentSession/hooks/useSafeAddress'

const useSidebarItems = (): ListItemType[] => {
  const featuresEnabled = useSelector(currentSafeFeaturesEnabled)
  const safeAppsEnabled = hasFeature(FEATURES.SAFE_APPS)
  const isCollectiblesEnabled = hasFeature(FEATURES.ERC721)
  const isSpendingLimitEnabled = hasFeature(FEATURES.SPENDING_LIMIT)
  const { needsUpdate } = useSelector(currentSafeWithNames)
  const { shortName, safeAddress } = useSafeAddress()
  const granted = useSelector(grantedSelector)

  const matchSafe = useRouteMatch(ADDRESSED_ROUTE)

  // Question mark makes matching [SAFE_SUBSECTION_SLUG] optional
  const matchSafeWithSidebarSection = useRouteMatch(`${SAFE_SUBSECTION_ROUTE}?`)

  const makeEntryItem = useCallback(
    ({ label, disabled, badge, iconType, href, subItems }) => ({
      label,
      badge,
      disabled,
      icon: iconType ? <i className="material-icons">{iconType}</i> : iconType,
      selected: href === matchSafeWithSidebarSection?.url,
      href,
      subItems,
    }),
    [matchSafeWithSidebarSection],
  )

  return useMemo((): ListItemType[] => {
    if (!matchSafe || !matchSafeWithSidebarSection || !featuresEnabled || !safeAddress) {
      return []
    }

    const currentSafeRoutes = generatePrefixedAddressRoutes({
      shortName,
      safeAddress,
    })

    const assetsSubItems = [
      makeEntryItem({
        label: 'Coins',
        iconType: 'fiber_smart_record',
        href: currentSafeRoutes.ASSETS_BALANCES,
      }),
      makeEntryItem({
        disabled: !isCollectiblesEnabled,
        label: 'NFTs',
        iconType: 'diamond',
        href: currentSafeRoutes.ASSETS_BALANCES_COLLECTIBLES,
      }),
    ]

    const transactionsSubItems = [
      makeEntryItem({
        label: 'Queue',
        iconType: 'queue',
        href: currentSafeRoutes.TRANSACTIONS_QUEUE,
      }),
      makeEntryItem({
        label: 'History',
        iconType: 'history',
        href: currentSafeRoutes.TRANSACTIONS_HISTORY,
      }),
    ]

    const settingsSubItems = [
      makeEntryItem({
        label: 'Info',
        badge: needsUpdate && granted,
        iconType: 'info',
        href: currentSafeRoutes.SETTINGS_DETAILS,
      }),
      // makeEntryItem({
      //   label: 'Appearance',
      //   iconType: 'preview',
      //   href: currentSafeRoutes.SETTINGS_APPEARANCE,
      // }),
      makeEntryItem({
        label: 'Owners',
        iconType: 'group',
        href: currentSafeRoutes.SETTINGS_OWNERS,
      }),
      makeEntryItem({
        label: 'Policies',
        iconType: 'history_edu',
        href: currentSafeRoutes.SETTINGS_POLICIES,
      }),
      // makeEntryItem({
      //   disabled: !isSpendingLimitEnabled,
      //   label: 'Spending Limit',
      //   iconType: 'speed',
      //   href: currentSafeRoutes.SETTINGS_SPENDING_LIMIT,
      // }),
      makeEntryItem({
        label: 'Advanced',
        iconType: 'build',
        href: currentSafeRoutes.SETTINGS_ADVANCED,
      }),
    ].filter(Boolean)

    return [
      makeEntryItem({
        label: 'Home',
        iconType: 'home',
        href: currentSafeRoutes.DASHBOARD,
      }),
      makeEntryItem({
        label: 'Assets',
        iconType: 'fiber_smart_record',
        href: currentSafeRoutes.ASSETS_BALANCES,
        subItems: assetsSubItems,
      }),
      makeEntryItem({
        label: 'Transactions',
        iconType: 'import_export',
        href: currentSafeRoutes.TRANSACTIONS_HISTORY,
        subItems: transactionsSubItems,
      }),
      makeEntryItem({
        label: 'Contacts',
        iconType: 'menu_book',
        href: currentSafeRoutes.ADDRESS_BOOK,
      }),
      makeEntryItem({
        disabled: !safeAppsEnabled,
        label: 'Apps',
        iconType: 'hdr_weak',
        href: currentSafeRoutes.APPS,
      }),
      makeEntryItem({
        disabled: !safeAppsEnabled,
        label: 'Walliro Biz',
        iconType: 'account_balance_wallet',
        href: currentSafeRoutes.BIZ,
      }),
      makeEntryItem({
        label: 'Profile',
        iconType: 'settings',
        href: currentSafeRoutes.SETTINGS_DETAILS,
        // subItems: settingsSubItems,
      }),
    ]
  }, [
    featuresEnabled,
    granted,
    isCollectiblesEnabled,
    isSpendingLimitEnabled,
    makeEntryItem,
    matchSafe,
    matchSafeWithSidebarSection,
    needsUpdate,
    safeAddress,
    safeAppsEnabled,
    shortName,
  ])
}

export { useSidebarItems }
